import { useEffect, useRef, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { MessageCircle, X, Send, Loader2, Sparkles } from "lucide-react";

const GREETING = "Hi! I'm Hoomi, your Hoomanz concierge. Ask me anything about booking, payouts, verification, promotions — I'm here to help.";

const SUGGESTED = [
  "How do payouts work?",
  "How do I get verified?",
  "How does the 5% fee work?",
  "How do online consultations work?",
];

export default function HelpBot() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState(() => [{ role: "assistant", text: GREETING }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [session, setSession] = useState(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => { if (open && inputRef.current) inputRef.current.focus(); }, [open]);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [msgs, busy]);

  const ask = async (text) => {
    const q = (text || input).trim();
    if (!q || busy) return;
    setInput("");
    const nextMsgs = [...msgs, { role: "user", text: q }];
    setMsgs(nextMsgs);
    setBusy(true);
    try {
      // Send only the last ~6 turns as history — Claude gets recent context, we stay light.
      const history = nextMsgs.slice(-7, -1);
      const { data } = await api.post("/help/chat", { message: q, history, session_id: session });
      if (data.session_id) setSession(data.session_id);
      setMsgs(m => [...m, { role: "assistant", text: data.reply }]);
    } catch (e) {
      setMsgs(m => [...m, { role: "assistant", text: `Sorry — ${formatErr(e)}. Try again, or email hello@hoomanz.co.` }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {/* Floating action button */}
      {!open && (
        <button onClick={()=>setOpen(true)} data-testid="helpbot-fab"
          className="fixed bottom-5 right-5 z-40 h-14 w-14 rounded-full bg-[#050505] text-white shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all inline-flex items-center justify-center"
          aria-label="Open help chat">
          <MessageCircle size={22}/>
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div className="fixed inset-x-4 bottom-4 sm:inset-x-auto sm:right-5 sm:bottom-5 sm:w-[380px] z-40 bg-white rounded-3xl shadow-2xl border border-[#E8E8E8] flex flex-col overflow-hidden"
          style={{ height: "min(560px, calc(100vh - 32px))" }}
          data-testid="helpbot-panel">
          {/* Header */}
          <div className="flex items-center gap-2 px-4 py-3 border-b border-[#E8E8E8] bg-[#050505] text-white">
            <span className="h-8 w-8 rounded-full bg-[#0057FF] inline-flex items-center justify-center">
              <Sparkles size={14}/>
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium leading-tight">Hoomi</p>
              <p className="text-[11px] text-white/60 leading-tight">Your Hoomanz concierge</p>
            </div>
            <button onClick={()=>setOpen(false)} aria-label="Close chat"
              className="text-white/70 hover:text-white" data-testid="helpbot-close">
              <X size={16}/>
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3 text-sm" data-testid="helpbot-messages">
            {msgs.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 whitespace-pre-wrap leading-relaxed ${
                  m.role === "user" ? "bg-[#0057FF] text-white" : "bg-[#F5F5F5] text-[#050505]"
                }`} data-testid={`helpbot-msg-${m.role}`}>
                  {m.text}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start">
                <div className="bg-[#F5F5F5] rounded-2xl px-3.5 py-2.5 inline-flex items-center gap-2 text-[#454545]">
                  <Loader2 size={13} className="animate-spin"/> <span className="text-xs">Thinking…</span>
                </div>
              </div>
            )}
          </div>

          {/* Suggested prompts (only shown before first user msg) */}
          {msgs.filter(m => m.role === "user").length === 0 && !busy && (
            <div className="px-4 pb-2 flex flex-wrap gap-1.5" data-testid="helpbot-suggestions">
              {SUGGESTED.map(s => (
                <button key={s} onClick={()=>ask(s)}
                  className="text-[11px] px-2.5 py-1.5 rounded-full bg-[#F5F5F5] hover:bg-[#0057FF] hover:text-white transition-colors text-[#454545]">
                  {s}
                </button>
              ))}
            </div>
          )}

          {/* Composer */}
          <form onSubmit={(e)=>{ e.preventDefault(); ask(); }}
            className="border-t border-[#E8E8E8] p-3 flex gap-2 items-end">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e)=>setInput(e.target.value)}
              onKeyDown={(e)=>{
                if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); ask(); }
              }}
              rows={1}
              maxLength={1000}
              placeholder="Ask Hoomi anything…"
              className="flex-1 resize-none rounded-2xl border border-[#E8E8E8] px-3.5 py-2.5 text-sm outline-none focus:border-[#0057FF] focus:ring-2 focus:ring-[#0057FF]/20 max-h-24"
              data-testid="helpbot-input"
              disabled={busy}
            />
            <button type="submit" disabled={busy || !input.trim()}
              className="h-9 w-9 rounded-full bg-[#050505] text-white disabled:opacity-40 hover:bg-[#1A1A1A] transition-colors inline-flex items-center justify-center flex-shrink-0"
              data-testid="helpbot-send"
              aria-label="Send message">
              {busy ? <Loader2 size={14} className="animate-spin"/> : <Send size={14}/>}
            </button>
          </form>
        </div>
      )}
    </>
  );
}
